/**
 * Self-schedule buffer field. Renders one of three presentations:
 *   - Read-only pill: "Buffer: 15m before + 15m after" + edit + eye.
 *   - Edit: two `NumberStepper` inputs (Before / After) + eye toggle.
 *   - Disabled (eye off): pill greyed; eye re-enables.
 *
 * The inner steppers reuse the library's `NumberStepper` atom — same
 * widget FormBuilder mounts for `type: 'numberStepper'` fields.
 */
import { useState } from 'react';
import { Box, Flex, IconButton } from '@chakra-ui/react';
import { NumberStepper, Text } from '@kula-ai/components';
import { Edit05, Eye, EyeOff } from '@kula-ai/icons-core';
import {
  BUFFER_MAX_MINUTES,
  BUFFER_MIN_MINUTES,
  BUFFER_STEP_MINUTES,
  DEFAULT_BUFFER_AFTER_MINUTES,
  DEFAULT_BUFFER_BEFORE_MINUTES,
} from '../buffer';

export interface BufferFieldValue {
  before: number;
  after: number;
  enabled: boolean;
}

export interface BufferFieldProps {
  value: BufferFieldValue;
  onChange: (_next: BufferFieldValue) => void;
}

/** "1 min" / "3 mins" — short form with correct singular/plural. */
const formatMins = (n: number) => `${n} ${n === 1 ? 'min' : 'mins'}`;

export function BufferField({ value, onChange }: BufferFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const { before, after, enabled } = value;

  const toggleEnabled = () => {
    // Enabling from a fresh 0/0 (e.g. a new account where the BE returns
    // 0/0/false) seeds sensible defaults so the pill doesn't read
    // "0 mins before + 0 mins after". This seed lives ONLY on the
    // enable transition — editing the steppers down to 0 while already
    // enabled is respected and never snapped back to the default.
    if (!enabled && before === 0 && after === 0) {
      onChange({
        before: DEFAULT_BUFFER_BEFORE_MINUTES,
        after: DEFAULT_BUFFER_AFTER_MINUTES,
        enabled: true,
      });
      return;
    }
    onChange({ ...value, enabled: !enabled });
  };

  return (
    <Box
      w="100%"
      padding={isEditing && enabled ? '0' : '12px 17px'}
      bg={isEditing && enabled ? 'transparent' : 'secondary-25'}
      borderRadius="md"
    >
      {isEditing && enabled ? (
        <Flex gap="2">
          <Flex direction="column" gap="1" flex="1" minW={0}>
            <Flex align="center" minH="24px">
              <Text variant="label" color="heading">
                Before meeting buffer
              </Text>
            </Flex>
            <NumberStepper
              value={before}
              min={BUFFER_MIN_MINUTES}
              max={BUFFER_MAX_MINUTES}
              step={BUFFER_STEP_MINUTES}
              unitLabel="mins"
              onChange={(n) => onChange({ ...value, before: n })}
            />
          </Flex>
          <Flex direction="column" gap="1" flex="1" minW={0}>
            <Flex align="center" justify="space-between" gap="2" minH="24px">
              <Text variant="label" color="heading">
                After meeting buffer
              </Text>
              <IconButton
                aria-label="Hide buffer"
                size="xs"
                variant="ghost"
                color="secondary-700"
                icon={<Eye size={16} />}
                onClick={toggleEnabled}
              />
            </Flex>
            <NumberStepper
              value={after}
              min={BUFFER_MIN_MINUTES}
              max={BUFFER_MAX_MINUTES}
              step={BUFFER_STEP_MINUTES}
              unitLabel="mins"
              onChange={(n) => onChange({ ...value, after: n })}
            />
          </Flex>
        </Flex>
      ) : (
        <Flex align="center" justify="space-between" gap="3">
          <Flex align="center" gap="1.5" minW={0}>
            <Text
              variant="label"
              color={enabled ? 'heading' : 'disabled'}
              style={{ flexShrink: 0 }}
            >
              Buffer:
            </Text>
            <Text variant="p" color={enabled ? 'heading' : 'disabled'} truncate>
              {enabled
                ? `${formatMins(before)} before + ${formatMins(after)} after`
                : `Disabled (${formatMins(before)} / ${formatMins(after)})`}
            </Text>
          </Flex>
          <Flex align="center" gap="1.5" flexShrink={0}>
            {enabled && (
              <IconButton
                aria-label="Edit buffer"
                size="xs"
                variant="ghost"
                color="secondary-700"
                icon={<Edit05 size={16} />}
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
                onClick={() => setIsEditing(true)}
              />
            )}
            <IconButton
              aria-label={enabled ? 'Hide buffer' : 'Show buffer'}
              size="xs"
              variant="ghost"
              color={enabled ? 'secondary-700' : 'primary-500'}
              icon={enabled ? <Eye size={16} /> : <EyeOff size={16} />}
              onClick={toggleEnabled}
            />
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
